import { get } from "lodash";
import { STATE } from "../../thor.const";
import { auth as authT } from "../../typings/auth";
import { thor } from "../../typings/thor";
import { EnumHelpers } from "../../utils/enum-helpers";
import { formatDeviceId } from "./formatDeviceId.filter";
import { StorageKey } from "../../thor.enums";

class AuthService {

    public static $inject: string[] = [
        "$injector",
        "$log",
        "$q",
        "$translate",
        "$window",
        "configService",
        "utilsService"
    ];

    public jwtDeviceId: string = null;

    private role;
    private organization: thor.Organization;
    private organizationId;
    private device;
    private readonly deviceId: string | null = null;
    private firstname;
    private lastname;
    private rights: authT.Rights[] = [];

    constructor(
        private $injector: ng.auto.IInjectorService,
        private $log: ng.ILogService,
        private $q: ng.IQService,
        private $translate: ng.translate.ITranslateService,
        $window: ng.IWindowService,
        private configService,
        private utilsService
    ) {
        try {
            this.deviceId = formatDeviceId(this.utilsService.decodeToken(this.getAccessToken()).deviceId);
        } catch (error) {
            this.$log.warn("Unable to get device id", { error });

            const { pathname, search } = $window.location;
            const afterLoginRedirect = localStorage.getItem(StorageKey.AfterLoginRedirect);

            if (pathname !== STATE.V2.LOGIN.url && !afterLoginRedirect) {
                localStorage.setItem(StorageKey.AfterLoginRedirect, `${pathname}${search}`);
            }
        }
    }

    public getRefreshToken() {
        return localStorage.getItem("refreshToken");
    }

    public setRefreshToken(refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
    }

    public getAccessToken() {
        // Ensure that you have a refreshToken before getting the accessToken
        if (this.getRefreshToken()) {
            return localStorage.getItem("accessToken");
        } else {
            this.removeAccessToken();

            return null;
        }
    }

    public setAccessToken(accessToken) {
        localStorage.setItem("accessToken", accessToken);
    }

    public getRole() {
        return this.role;
    }

    public getOrganizationId() {
        return this.organizationId;
    }

    public getDevice() {
        return this.device;
    }

    public getFirstName() {
        return this.firstname;
    }

    public getLastName() {
        return this.lastname;
    }

    public isAdmin() {
        return this.role === authT.Roles.Admin;
    }

    public getUserRights(): authT.HasRights {
        return this.formatRights(this.rights);
    }

    public refreshAccessToken() {
        const refreshToken = this.getRefreshToken();

        if (!refreshToken) {
            return this.$q.reject({ redirectStateName: STATE.V2.LOGIN.name });
        }

        const deferred = this.$q.defer();
        this.$injector.get("$http")
            .get(
                this.configService.authApi.url + "/v1/auth/access",
                {
                   headers: { "x-cpm-refresh-token": refreshToken }
                }
            )
            .then((res: any) => {
                if (res && res.status === 200 && res.data.accessToken) {
                    this.setAccessToken(res.data.accessToken);
                    return this._loadAccessTokenData()
                        .then(() => {
                            deferred.resolve();
                        })
                        .catch(error => {
                            deferred.reject({ error });
                        });
                } else {
                    deferred.reject({ redirectStateName: STATE.V2.LOGIN.name });
                }
            })
            .catch(error => {
                if (error.status === 423) {
                    deferred.reject({ redirectStateName: STATE.ERROR.MAIN });
                } else {
                    deferred.reject({ redirectStateName: STATE.V2.LOGIN.name, error });
                }
            });

        return deferred.promise;
    }

    public fetchOrganization() {
        if (this.organizationId) {
            return (this.$injector.get("organizationService") as any)
                .getFullOrganization(this.organizationId)
                .then(organization => {
                    this.organization = organization;
                    return this.$q.resolve();
                })
                .catch(error => {
                    this.$q.reject({
                        error,
                        toastMessage: this.$translate.instant(
                            "impossible_de_recuperer_votre_etude"
                        ),
                        warn: true
                    });
                });
        }
    }

    public formatRights(rights: authT.Rights[]): authT.HasRights {
        const scopes = EnumHelpers.getValues(authT.Scopes);
        const hasRights = {};

        scopes.forEach((scope: authT.Scopes) => {
            hasRights[scope] = false;

            if (rights) {
                rights.some((right: authT.Rights) => hasRights[scope] = right.scopes.includes(scope));
            }
        });
        return hasRights;
    }

    public getLogin() {
        return get(this.organization, "login");
    }

    public getDeviceId() {
        return this.deviceId;
    }

    public removeAccessToken() {
        localStorage.removeItem("accessToken");
    }

    public removeRefreshToken() {
        localStorage.removeItem("refreshToken");
    }

    public isInternational() {
        const dromComPattern = /^(97|98|99)\d{3}$/;
        const corsicaPattern = /^20\d{3}$/;

        return (
            this.organization.address.country !== "FR" ||
            dromComPattern.test(this.organization.address.postcode) ||
            corsicaPattern.test(this.organization.address.postcode)
        );
    }

    private _loadAccessTokenData() {
        let jwtAccessToken: authT.JwtAccessToken;

        try {
            jwtAccessToken = this.utilsService.decodeToken(this.getAccessToken());
        } catch (error) {
            this.removeAccessToken();
            return this.$q.reject({ error });
        }

        if (!jwtAccessToken) {
            this.removeAccessToken();
            return this.$q.reject({error: "Corrupted access token, it has been deleted"});
        }

        if (Math.floor(Date.now() / 1000) > jwtAccessToken.exp) {
            return this.$q.reject({
                toastMessage: this.$translate.instant("connexion_expiree")
            });
        }

        this.role = jwtAccessToken.role;
        this.device = jwtAccessToken.device;
        this.jwtDeviceId = jwtAccessToken.deviceId;
        this.firstname = jwtAccessToken.firstname;
        this.lastname = jwtAccessToken.lastname;
        this.rights = jwtAccessToken.rights || [];

        if (this.role === authT.Roles.Admin) {
            if (sessionStorage.getItem("organizationId")) {
                this.organizationId = parseInt(sessionStorage.getItem("organizationId"), 10);
            }
        } else {
            this.organizationId = jwtAccessToken.organizationId;
        }

        return this.$q.resolve();
    }
}

angular
    .module("thor.core.auth.service", [])
    .service("authService", AuthService);
